import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
// Entferne den Import, da wir die Funktionen lokal definieren
// import { formatDate, parseDate } from '../utils.js';

const Absenzen = ({ projectId }) => {
  const [absences, setAbsences] = useState([]);
  const [actors, setActors] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [newAbsence, setNewAbsence] = useState({
    schauspieler: '',
    datum: '',
    anfangszeit: '',
    endzeit: '',
    bemerkungen: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'datum', direction: 'ascending' });

  // Hilfsfunktion: Formatiert Datum von YYYY-MM-DD zu DD.MM.YYYY für die Anzeige
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('.')) return dateStr; // Bereits im richtigen Format
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  };

  // Hilfsfunktion: Konvertiert Datum von DD.MM.YYYY zu YYYY-MM-DD für interne Verarbeitung
  const parseDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('-')) return dateStr; // Bereits im internen Format
    
    const parts = dateStr.split('.');
    if (parts.length !== 3) return dateStr;
    
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  // Hilfsfunktion: Gibt den Wochentag für ein gegebenes Datum zurück
  const getWeekday = (dateStr) => {
    const date = new Date(parseDate(dateStr));
    const weekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return weekdays[date.getDay()];
  };

  // Sortiert die Daten nach der angegebenen Spalte
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Gibt die sortierten Daten zurück
  const getSortedData = () => {
    if (!sortConfig.key) return absences;
    
    const sortableItems = [...absences];
    sortableItems.sort((a, b) => {
      // Spezielle Behandlung für Datumsfelder
      if (sortConfig.key === 'datum') {
        const dateA = parseDate(a.datum);
        const dateB = parseDate(b.datum);
        
        if (sortConfig.direction === 'ascending') {
          return new Date(dateA) - new Date(dateB);
        } else {
          return new Date(dateB) - new Date(dateA);
        }
      }
      
      // Standardsortierung für andere Felder
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };

  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        console.log('Loading absences data for project:', projectId); // Debug-Log hinzufügen
        
        const savedAbsences = localStorage.getItem(`absences_${projectId}`);
        if (savedAbsences) {
          try {
            const parsedAbsences = JSON.parse(savedAbsences);
            if (parsedAbsences && parsedAbsences.length > 0) {
              console.log('Loaded absences:', parsedAbsences); // Debug-Log hinzufügen
              setAbsences(parsedAbsences);
            } else {
              console.log('No absences found or empty array'); // Debug-Log hinzufügen
            }
          } catch (error) {
            console.error('Error loading absences:', error);
          }
        } else {
          console.log('No absences data found in localStorage'); // Debug-Log hinzufügen
        }

        const savedActors = localStorage.getItem(`actorEnsemble_${projectId}`);
        if (savedActors) {
          try {
            const parsedActors = JSON.parse(savedActors);
            if (parsedActors && parsedActors.length > 0) {
              setActors(parsedActors);
            }
          } catch (error) {
            console.error('Error loading actors:', error);
          }
        }
      }
    };

    loadSavedData();
  }, [projectId]);

  // Speichern der Absenzen, wenn sich der State ändert
  useEffect(() => {
    if (projectId && absences.length > 0) {
      console.log('Saving absences:', absences); // Debug-Log hinzufügen
      localStorage.setItem(`absences_${projectId}`, JSON.stringify(absences));
    }
  }, [absences, projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAbsence(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCellEdit = (id, field, value) => {
    setAbsences(prev => 
      prev.map(absence => 
        absence.id === id ? { ...absence, [field]: value } : absence
      )
    );
    setEditingCell(null);
  };

  const saveAbsence = () => {
    // Jetzt können wir Absenzen auch ohne Zeitangaben speichern (für ganztägige Absenzen)
    if (newAbsence.schauspieler && newAbsence.datum) {
      // Sicherstellen, dass die ID eindeutig ist
      const uniqueId = Date.now().toString();
      
      const absenceToAdd = { 
        ...newAbsence,
        id: uniqueId,
        // Formatieren des Datums für die Anzeige
        datum: formatDate(newAbsence.datum)
      };
      
      console.log('Adding new absence:', absenceToAdd); // Debug-Log hinzufügen
      
      // Verwenden Sie eine callback-Funktion, um sicherzustellen, dass der aktuelle State verwendet wird
      setAbsences(prevAbsences => {
        const updatedAbsences = [...prevAbsences, absenceToAdd];
        
        // Sofort in localStorage speichern
        if (projectId) {
          localStorage.setItem(`absences_${projectId}`, JSON.stringify(updatedAbsences));
        }
        
        return updatedAbsences;
      });
      
      // Formular zurücksetzen
      setNewAbsence({
        schauspieler: '',
        datum: '',
        anfangszeit: '',
        endzeit: '',
        bemerkungen: ''
      });
    }
  };

  const deleteAbsence = (id) => {
    if (window.confirm('Möchten Sie diese Absenz wirklich löschen?')) {
      setAbsences(prev => {
        const updatedAbsences = prev.filter(absence => absence.id !== id);
  
        if (projectId) {
          if (updatedAbsences.length === 0) {
            localStorage.removeItem(`absences_${projectId}`);
          } else {
            localStorage.setItem(`absences_${projectId}`, JSON.stringify(updatedAbsences));
          }
        }
  
        return updatedAbsences;
      });
    }
  };
  
  const exportToCSV = () => {
    const csv = Papa.unparse(absences);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'absenzen.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let data;
      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        const results = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        data = results.data;
      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, {
          type: 'array',
          dateNOS: true,
          cellDates: true
        });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        data = XLSX.utils.sheet_to_json(worksheet, {
          raw: false,
          dateNF: 'YYYY-MM-DD'
        });
      }

      const newAbsences = data
        .filter(absence => (absence.schauspieler || absence.Schauspieler) && 
                          (absence.datum || absence.Datum))
        .map(absence => {
          let datum = absence.datum || absence.Datum || '';
          
          // Formatieren des Datums für die Anzeige
          if (datum.includes('-')) {
            datum = formatDate(datum);
          }
          
          return {
            id: Date.now() + Math.random(),
            schauspieler: absence.schauspieler || absence.Schauspieler || '',
            datum: datum,
            anfangszeit: absence.anfangszeit || absence.Anfangszeit || '',
            endzeit: absence.endzeit || absence.Endzeit || '',
            bemerkungen: absence.bemerkungen || absence.Bemerkungen || ''
          };
        });

      setAbsences(prev => {
        const updatedAbsences = [...prev, ...newAbsences];
        
        // Sofort in localStorage speichern
        if (projectId) {
          localStorage.setItem(`absences_${projectId}`, JSON.stringify(updatedAbsences));
        }
        
        return updatedAbsences;
      });
      
      alert(`${newAbsences.length} Absenzen wurden erfolgreich importiert.`);

    } catch (error) {
      console.error('Fehler beim Datei-Import:', error);
      alert('Fehler beim Import der Datei. Bitte überprüfen Sie das Format.');
    }
    e.target.value = '';
  };

  // Die anzuzeigenden Daten sortieren
  const sortedAbsences = getSortedData();

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Absenzen</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <select
          name="schauspieler"
          value={newAbsence.schauspieler}
          onChange={handleInputChange}
          className="border p-2"
        >
          <option value="">Schauspieler auswählen</option>
          {actors.map((actor) => (
            <option key={actor.id} value={actor.name}>
              {actor.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          name="datum"
          value={newAbsence.datum}
          onChange={handleInputChange}
          placeholder="Datum"
          className="border p-2"
        />

        <input
          type="time"
          name="anfangszeit"
          value={newAbsence.anfangszeit}
          onChange={handleInputChange}
          placeholder="Anfangszeit"
          className="border p-2"
        />

        <input
          type="time"
          name="endzeit"
          value={newAbsence.endzeit}
          onChange={handleInputChange}
          placeholder="Endzeit"
          className="border p-2"
        />

        <input
          type="text"
          name="bemerkungen"
          value={newAbsence.bemerkungen}
          onChange={handleInputChange}
          placeholder="Bemerkungen"
          className="border p-2"
        />

        <button
          onClick={saveAbsence}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          disabled={!newAbsence.schauspieler || !newAbsence.datum}
        >
          Absenz speichern
        </button>
      </div>

      <div className="mb-4 flex space-x-2">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
          id="csvUpload"
        />
        <label 
          htmlFor="csvUpload" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
        >
          Excel importieren
        </label>
        <button
          onClick={exportToCSV}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
        >
          Als Excel exportieren
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border">
          <thead>
            <tr className="bg-gray-200">
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('schauspieler')}
              >
                Schauspieler {sortConfig.key === 'schauspieler' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('datum')}
              >
                Datum {sortConfig.key === 'datum' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th className="border p-2">Wochentag</th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('anfangszeit')}
              >
                Anfangszeit {sortConfig.key === 'anfangszeit' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('endzeit')}
              >
                Endzeit {sortConfig.key === 'endzeit' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th 
                className="border p-2 cursor-pointer hover:bg-gray-300"
                onClick={() => requestSort('bemerkungen')}
              >
                Bemerkungen {sortConfig.key === 'bemerkungen' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
              </th>
              <th className="border p-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {sortedAbsences.map((absence) => (
              <tr key={absence.id}>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: absence.id, field: 'schauspieler' })}
                >
                  {editingCell?.id === absence.id && editingCell?.field === 'schauspieler' ? (
                    <select
                      value={absence.schauspieler}
                      onChange={(e) => handleCellEdit(absence.id, 'schauspieler', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    >
                      {actors.map((actor) => (
                        <option key={actor.id} value={actor.name}>
                          {actor.name}
                        </option>
                      ))}
                    </select>
                  ) : absence.schauspieler}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: absence.id, field: 'datum' })}
                >
                  {editingCell?.id === absence.id && editingCell?.field === 'datum' ? (
                    <input
                      type="date"
                      value={parseDate(absence.datum)}
                      onChange={(e) => handleCellEdit(absence.id, 'datum', formatDate(e.target.value))}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : absence.datum}
                </td>
                <td className="border p-2">{getWeekday(absence.datum)}</td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: absence.id, field: 'anfangszeit' })}
                >
                  {editingCell?.id === absence.id && editingCell?.field === 'anfangszeit' ? (
                    <input
                      type="time"
                      value={absence.anfangszeit || ''}
                      onChange={(e) => handleCellEdit(absence.id, 'anfangszeit', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : absence.anfangszeit}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: absence.id, field: 'endzeit' })}
                >
                  {editingCell?.id === absence.id && editingCell?.field === 'endzeit' ? (
                    <input
                      type="time"
                      value={absence.endzeit || ''}
                      onChange={(e) => handleCellEdit(absence.id, 'endzeit', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : absence.endzeit}
                </td>
                <td 
                  className="border p-2 cursor-pointer"
                  onClick={() => setEditingCell({ id: absence.id, field: 'bemerkungen' })}
                >
                  {editingCell?.id === absence.id && editingCell?.field === 'bemerkungen' ? (
                    <input
                      type="text"
                      value={absence.bemerkungen || ''}
                      onChange={(e) => handleCellEdit(absence.id, 'bemerkungen', e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      autoFocus
                      className="w-full"
                    />
                  ) : absence.bemerkungen}
                </td>
                <td className="border p-2">
                  <button
                    onClick={() => deleteAbsence(absence.id)}
                    className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Absenzen;